import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { NAV_ITEMS } from './navigation';

/**
 * The §5.1 navigation.
 *
 * `aria-current="page"` marks the active destination; the visual treatment is
 * a background *and* a left border, never colour alone (design doc §5.6).
 * Destinations with no backend are marked in text, not only by styling, so a
 * screen-reader user learns the same thing a sighted user does.
 */
export function SideNav({ onNavigate }: { readonly onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { workspaceId } = useParams();

  return (
    <nav aria-label={t('app.mainNavigation')} className="p-2">
      <ul className="flex flex-col gap-0.5">
        {NAV_ITEMS.map((item) => (
          <li key={item.id}>
            <NavLink
              to={`/w/${workspaceId ?? ''}/${item.to}`}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-2 text-sm',
                  'hover:bg-accent hover:text-accent-foreground',
                  isActive && 'border-l-primary bg-accent font-semibold text-accent-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon aria-hidden="true" className="size-4 shrink-0" />
                  <span className="truncate">{t(item.labelKey)}</span>
                  {!item.built ? (
                    <span className="ml-auto rounded-sm border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {t('nav.notBuiltYet')}
                    </span>
                  ) : null}
                  {isActive ? <span className="sr-only">{t('nav.currentPage')}</span> : null}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
